import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import getConfig from 'next/config';
import { formatNumberForIntegralPart } from '../../../utils/number';
import { FlexBox } from '../../FlexBox';
import { STAKE_STATUS } from '../constants';
import { getLabelWithDefaultStr } from '../../../utils/i18n';
import type { StakeModel } from '../types';
import withI18n from '../../../utils/withI18n';
import { TradeButton, DepositButton } from './TradeButton';
import { getRemainDayStr } from '../utils';
import RateValue from './RateValue';

const {
  publicRuntimeConfig: { assetPrefix },
} = getConfig();

const lightTheme = {
  _borderColor: '#EAECEF',
  _titleColor: '#212833',
  _hoverStyle: 'box-shadow: 0px 0px 40px rgba(0, 0, 0, 0.04)',
  _background: 'white',
};

export default
@withI18n
class CardContainer extends React.PureComponent {
  render() {
    return <Card {...this.props} />;
  }
}

type CardProps = {
  i18n: Object,
  locale: string,
  onClick: Function,
  ...StakeModel,
};

const Card = ({
  i18n,
  locale = 'en',
  onClick = () => {},
  stakingName = '-------',
  returnOfRate = '0;0',
  leastHoldCoin = '0',
  holdCoin = 'XXX',
  startTime = '1970-01-01',
  status = STAKE_STATUS.upComing,
  tags,
}: CardProps) => {
  const logoUrl = `${assetPrefix}/static/images/coin/${holdCoin.toLowerCase()}.svg`;

  return (
    <ThemeProvider
      theme={theme => ({
        ...theme,
        ...lightTheme,
      })}
    >
      <Container onClick={onClick}>
        {status === STAKE_STATUS.upComing && (
          <Countdown>{getRemainDayStr(startTime, i18n)}</Countdown>
        )}
        <FirstRow>
          <CoinLogo>
            <img src={logoUrl} alt="coin" />
          </CoinLogo>
          <TitleDiv>
            <Title>{stakingName}</Title>
            {tags && (
              <Tags>
                {tags.split(';').map(tag => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </Tags>
            )}
          </TitleDiv>
        </FirstRow>
        <SecondRow>
          <Field>
            {getLabelWithDefaultStr('exchange-staking-return-rate', i18n, '参考年回报率')}:&nbsp;
            <RateValue returnOfRate={returnOfRate} />
          </Field>
          <Field>
            {getLabelWithDefaultStr('exchange-staking-least-hold-coin', i18n, '每日最低持仓')}
            :&nbsp;
            <HoldValue>
              {formatNumberForIntegralPart(leastHoldCoin)}&nbsp;{holdCoin}
            </HoldValue>
          </Field>
        </SecondRow>
        {status !== STAKE_STATUS.ended && (
          <ThirdRow>
            <DepositButton
              i18n={i18n}
              locale={locale}
              coin={holdCoin}
              size="md"
              type="primary"
              width={104}
            />
            <TradeButton i18n={i18n} locale={locale} baseAsset={holdCoin} size="md" width={104} />
          </ThirdRow>
        )}
      </Container>
    </ThemeProvider>
  );
};

const Container = styled.div`
  width: 376px;
  height: 220px;
  padding: 16px 0 24px 24px;
  border: 1px solid ${({ theme }) => theme._borderColor};
  border-radius: 10px;
  cursor: pointer;
  background-color: ${({ theme }) => theme._background};
  margin-bottom: 35px;
  position: relative;

  &:hover {
    ${({ theme }) => theme._hoverStyle};
  }

  &:nth-child(odd) {
    margin-left: 0px;
  }

  &:nth-child(even) {
    margin-right: 0px;
  }
`;

const Countdown = styled.div`
  position: absolute;
  right: 0;
  top: 0px;
  line-height: 22px;
  padding: 0 6px;
  color: #f23051;
  font-size: 12px;
  background: rgba(242, 48, 81, 0.1);
  border-top-right-radius: 10px;
  border-bottom-left-radius: 2px;
`;

const Field = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: #848e9c;
  margin-bottom: 14px;
`;

const HoldValue = styled.span`
  font-size: 14px;
  line-height: 16px;
  color: #48515d;
`;

const FirstRow = styled(FlexBox)`
  margin: 8px 0;
`;

const SecondRow = styled.div`
  font-size: 14px;
  margin-left: 55px;
`;

const ThirdRow = styled(FlexBox)`
  margin-left: 55px;
  margin-top: 18px;
`;

const CoinLogo = styled.div`
  height: 40px;
  width: 40px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme._borderColor};
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 80%;
    height: 80%;
  }
`;

const TitleDiv = styled.div`
  flex: 1;
  padding: 0 16px;
  height: 52px;
`;

const Title = styled.div`
  font-size: 18px;
  line-height: 23px;
  color: ${({ theme }) => theme._titleColor};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Tag = styled.div`
  margin: 5px 5px 0 0;
  color: #848e9c;
  border-radius: 16px;
  text-align: center;
  padding: 0 8px;
  font-size: 12px;
  line-height: 22px;
  border: 1px solid #eaecef;
`;
